import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  ClipboardList,
  Package,
  CalendarClock,
  UserRound,
  LogOut,
  ChevronDown,
  Menu,
  X,
  PackageOpen,
  Truck,
  AlertTriangle,
  BarChart3,
  Users,
  Layers,
  Tag,
  MapPin,
  Timer,
  CalendarDays,
} from "lucide-react";

import { useAuth } from "../providers/AuthProvider";
import { hasPermission, hasAnyPermission, Permission } from "../permissions";
import { LanguageSelector } from "../../components/layout/LanguageSelector";
import { InactivityGuard } from "../../components/session/InactivityGuard";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";

const NAV_ITEMS = [
  // Order finalised per user directive (2026-02).
  // `permission` accepts a single permission string OR an array (any-of).
  { to: "/dashboard", labelKey: "nav.dashboard", icon: LayoutDashboard, permission: Permission.DASHBOARD_VIEW, testId: "nav-dashboard", exact: true },
  { to: "/users", labelKey: "admin.nav.users", icon: Users, permission: Permission.ADMIN_USERS_MANAGE, testId: "nav-users" },
  { to: "/orders", labelKey: "nav.orders", icon: ClipboardList, permission: [Permission.ORDERS_VIEW_OWN, Permission.ORDERS_VIEW_ALL], testId: "nav-orders" },
  { to: "/products", labelKey: "nav.products", icon: Package, permission: Permission.PRODUCTS_VIEW, testId: "nav-products" },
  { to: "/categories", labelKey: "admin.nav.categories", icon: Layers, permission: Permission.ADMIN_CATEGORIES_MANAGE, testId: "nav-categories" },
  { to: "/brands", labelKey: "admin.nav.brands", icon: Tag, permission: Permission.ADMIN_BRANDS_MANAGE, testId: "nav-brands" },
  { to: "/availability", labelKey: "nav.availability", icon: CalendarClock, permission: Permission.AVAILABILITY_VIEW, testId: "nav-availability" },
  { to: "/delivery", labelKey: "admin.nav.delivery", icon: Truck, permission: Permission.ADMIN_DELIVERY_MANAGE, testId: "nav-delivery" },
  { to: "/collection", labelKey: "admin.nav.collection", icon: PackageOpen, permission: Permission.ADMIN_COLLECTION_MANAGE, testId: "nav-collection" },
  { to: "/damages", labelKey: "admin.nav.damages", icon: AlertTriangle, permission: [Permission.ADMIN_DAMAGES_MANAGE, Permission.DAMAGES_VIEW], testId: "nav-damages" },
  { to: "/reports", labelKey: "admin.nav.reports", icon: BarChart3, permission: Permission.ADMIN_REPORTS_VIEW, testId: "nav-reports" },
  { to: "/addresses", labelKey: "admin.nav.addresses", icon: MapPin, permission: Permission.ADMIN_ADDRESSES_MANAGE, testId: "nav-addresses" },
  { to: "/delivery-times", labelKey: "admin.nav.delivery_times", icon: Timer, permission: Permission.ADMIN_DELIVERY_TIMES_MANAGE, testId: "nav-delivery-times" },
  { to: "/holidays", labelKey: "admin.nav.holidays", icon: CalendarDays, permission: Permission.ADMIN_PUBLIC_HOLIDAYS_MANAGE, testId: "nav-holidays" },
];

/**
 * Single-aside, CSS-driven responsive sidebar.
 *   - < md (below 768 px): aside hidden; hamburger opens overlay copy.
 *   - md–lg (768–1023 px): 64 px icon rail, labels are `sr-only`,
 *     tooltips shown on hover via Radix Tooltip (its content is hidden
 *     at lg+ via `lg:hidden`).
 *   - ≥ lg (1024 px): 256 px column with labels; tooltips disabled.
 *
 * This is CSS-only responsive — no `matchMedia` state — so nothing
 * depends on JS timing.
 */
function NavItem({ item, forceExpanded = false, onNavigate }) {
  const { t } = useTranslation();
  const Icon = item.icon;
  const link = (
    <NavLink
      to={item.to}
      end={item.exact ?? item.to === "/dashboard"}
      data-testid={item.testId}
      onClick={onNavigate}
      aria-label={t(item.labelKey)}
      className={({ isActive }) =>
        [
          "relative flex items-center rounded-md text-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-champagne",
          forceExpanded
            ? "gap-3 px-3 py-2"
            : "justify-center px-2 py-2 lg:justify-start lg:gap-3 lg:px-3",
          isActive
            ? "bg-white/[0.06] text-white font-medium before:absolute before:left-0 before:top-1 before:bottom-1 before:w-[3px] before:rounded-r-sm before:bg-champagne"
            : "text-white/70 hover:bg-white/5 hover:text-white",
        ].join(" ")
      }
    >
      <Icon className="h-[18px] w-[18px] shrink-0" aria-hidden />
      {forceExpanded ? (
        <span>{t(item.labelKey)}</span>
      ) : (
        <span className="sr-only lg:not-sr-only lg:inline">{t(item.labelKey)}</span>
      )}
    </NavLink>
  );

  if (forceExpanded) return <li>{link}</li>;
  return (
    <li>
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        {/* Tooltip is meaningful only at md–lg; hide content at lg+. */}
        <TooltipContent side="right" sideOffset={12} className="lg:hidden">
          {t(item.labelKey)}
        </TooltipContent>
      </Tooltip>
    </li>
  );
}

function SidebarHeaderBlock({ forceExpanded = false }) {
  const { t } = useTranslation();
  return (
    <Link
      to="/dashboard"
      data-testid="sidebar-brand-link"
      aria-label={t("common.app_name")}
      className={
        forceExpanded
          ? "flex h-16 items-center gap-3 border-b border-white/10 px-4 transition-colors hover:bg-white/5 focus-visible:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne"
          : "flex h-16 items-center justify-center border-b border-white/10 px-3 transition-colors hover:bg-white/5 focus-visible:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne lg:justify-start lg:gap-3 lg:px-4"
      }
    >
      <div
        aria-hidden
        className="h-8 w-8 rounded-sm bg-champagne flex items-center justify-center text-charcoal font-semibold shrink-0"
      >
        MH
      </div>
      <div
        className={
          forceExpanded
            ? "leading-tight min-w-0"
            : "hidden lg:block leading-tight min-w-0"
        }
      >
        <div className="text-sm font-semibold tracking-wide truncate">
          {t("common.app_name")}
        </div>
        <div className="text-[11px] text-white/50 truncate">
          {t("common.app_subtitle")}
        </div>
      </div>
    </Link>
  );
}

/**
 * Section divider + label for the Administration cluster inside the
 * sidebar.
 *
 * Deprecated in A1.5 — the sidebar is now a single flat list. Left in
 * the file as a no-op export in case any snapshot tests still import
 * it. Safe to delete in A2.
 */
// Removed AdminSectionHeading + NavSections (A1.5 flat-nav pivot).

export function AppShell() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);

  const items = NAV_ITEMS.filter((item) =>
    Array.isArray(item.permission)
      ? hasAnyPermission(user, item.permission)
      : hasPermission(user, item.permission)
  );

  const initials = (user?.name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");

  useEffect(() => {
    // Only respond to route (pathname) changes, NOT to in-page ?query
    // updates — otherwise the URL-synced filters (Products, Orders) would
    // close their own popovers on every checkbox click.
    setOverlayOpen(false);
    try {
      document.dispatchEvent(
        new KeyboardEvent("keydown", { key: "Escape", bubbles: true })
      );
    } catch {
      /* SSR-safe no-op */
    }
  }, [location.pathname]);

  return (
    <InactivityGuard>
      <TooltipProvider delayDuration={200}>
        <div className="flex min-h-screen bg-offwhite" data-testid="app-shell">
          {/* Single responsive sidebar */}
          <aside
            className="hidden shrink-0 flex-col bg-charcoal text-white md:flex md:w-16 lg:w-64"
            data-testid="app-sidebar"
            aria-label={t("nav.dashboard")}
          >
            <SidebarHeaderBlock />
            <nav className="flex-1 px-2 py-6" data-testid="sidebar-nav">
              <ul className="space-y-1">
                {items.map((item) => (
                  <NavItem key={item.to} item={item} />
                ))}
              </ul>
            </nav>
            {/* Footnote — expanded only. */}
            <div className="hidden border-t border-white/10 p-4 text-[11px] text-white/40 lg:block">
              {t("auth.internal_only_note")}
            </div>
          </aside>

          {/* Overlay expansion — hamburger; hidden at lg+ */}
          {overlayOpen && (
            <div
              className="fixed inset-0 z-40 lg:hidden"
              data-testid="sidebar-overlay-root"
              role="dialog"
              aria-modal="true"
              aria-label={t("nav.open_menu")}
            >
              <button
                type="button"
                onClick={() => setOverlayOpen(false)}
                className="absolute inset-0 bg-black/50"
                aria-label={t("nav.close_menu")}
                data-testid="sidebar-overlay-scrim"
              />
              <aside
                className="relative flex h-full w-64 flex-col bg-charcoal text-white shadow-2xl"
                data-testid="sidebar-overlay"
              >
                <SidebarHeaderBlock forceExpanded />
                <nav className="flex-1 px-2 py-6">
                  <ul className="space-y-1">
                    {items.map((item) => (
                      <NavItem
                        key={item.to}
                        item={item}
                        forceExpanded
                        onNavigate={() => setOverlayOpen(false)}
                      />
                    ))}
                  </ul>
                </nav>
                <div className="border-t border-white/10 p-4 text-[11px] text-white/40">
                  {t("auth.internal_only_note")}
                </div>
              </aside>
            </div>
          )}

          {/* Main column */}
          <div className="flex min-w-0 flex-1 flex-col">
            <header
              className="flex h-16 items-center justify-between gap-2 border-b border-border-subtle bg-surface px-4 sm:px-6"
              data-testid="app-header"
            >
              <div className="flex min-w-0 items-center gap-2">
                <button
                  type="button"
                  onClick={() => setOverlayOpen((v) => !v)}
                  aria-label={overlayOpen ? t("nav.close_menu") : t("nav.open_menu")}
                  data-testid="sidebar-toggle"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border-subtle bg-surface text-text-primary transition-colors hover:bg-offwhite focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne lg:hidden"
                >
                  {overlayOpen ? (
                    <X className="h-4 w-4" aria-hidden />
                  ) : (
                    <Menu className="h-4 w-4" aria-hidden />
                  )}
                </button>
                <Link
                  to="/dashboard"
                  data-testid="header-brand-link"
                  className="flex min-w-0 items-center gap-2 md:hidden rounded-md transition-colors hover:bg-offwhite focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne"
                  aria-label={t("common.app_name")}
                >
                  <div
                    aria-hidden
                    className="h-7 w-7 shrink-0 rounded-sm bg-charcoal text-white flex items-center justify-center text-xs font-semibold"
                  >
                    MH
                  </div>
                  <span className="truncate text-sm font-semibold">
                    {t("common.app_name")}
                  </span>
                </Link>
              </div>

              <div className="flex items-center gap-2 sm:gap-4">
                <LanguageSelector variant="light" />

                <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      data-testid="user-menu-trigger"
                      className="flex items-center gap-2 rounded-md border border-border-subtle bg-surface px-2 py-1.5 text-sm text-text-primary transition-colors hover:bg-offwhite focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne"
                      aria-label={`${t("user_menu.signed_in_as")} ${user?.name || ""}`}
                    >
                      <span
                        aria-hidden
                        className="flex h-7 w-7 items-center justify-center rounded-full bg-charcoal text-[11px] font-semibold text-white"
                      >
                        {initials}
                      </span>
                      <span className="hidden max-w-[140px] truncate sm:inline">
                        {user?.name}
                      </span>
                      <ChevronDown className="h-4 w-4 text-text-secondary" aria-hidden />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    data-testid="user-menu-content"
                    className="w-60"
                  >
                    <DropdownMenuLabel>
                      <div className="text-[11px] uppercase tracking-wide text-text-secondary">
                        {t("user_menu.signed_in_as")}
                      </div>
                      <div className="mt-0.5 truncate text-sm font-medium text-text-primary">
                        {user?.name}
                      </div>
                      <div className="truncate text-xs text-text-secondary">
                        {user?.email}
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      data-testid="user-menu-profile"
                      onSelect={() => navigate("/profile")}
                    >
                      <UserRound className="mr-2 h-4 w-4" aria-hidden />
                      {t("nav.profile")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      data-testid="logout-button"
                      onSelect={() => logout()}
                      className="text-status-unavailable focus:text-status-unavailable"
                    >
                      <LogOut className="mr-2 h-4 w-4" aria-hidden />
                      {t("common.logout")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </header>

            <main className="flex-1 overflow-x-hidden bg-offwhite">
              <div
                className="mx-auto w-full max-w-[1400px] px-4 py-6 sm:px-6 sm:py-8 animate-fade-in"
                data-testid="app-main"
              >
                <Outlet />
              </div>
            </main>
          </div>
        </div>
      </TooltipProvider>
    </InactivityGuard>
  );
}
